







































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { updateHtmlLang } from '@/plugins/i18n';

interface MenuItem {
  label: string;
  path: string;
  ariaLabel: string;
}

interface Language {
  code: string;
  name: string;
}

@Component
export default class MobileMenu extends Vue {
  isMenuOpen = false;
  showLanguageMenu = false;
  backButton: HTMLElement | null = null;
  languageMenuItem: HTMLElement | null = null;
  menuButton: HTMLElement | null = null;
  focusedItemClass = 'menu-item--focused';
  openedViaKeyboard = false;

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  }

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside(event: MouseEvent) {
    if (!this.isMenuOpen) return;

    const target = event.target as HTMLElement;
    const menuList = this.$refs.menuList as HTMLElement;

    if (!this.menuButtonContains(target) && !this.menuListContains(target, menuList)) {
      this.closeMenu();
    }
  }

  private menuButtonContains(target: HTMLElement): boolean {
    return !!this.menuButton && this.menuButton.contains(target);
  }

  private menuListContains(target: HTMLElement, menuList: HTMLElement | null): boolean {
    return !!menuList && menuList.contains(target);
  }

  toggleMenu() {
    this.openedViaKeyboard = false;
    this.isMenuOpen = !this.isMenuOpen;
  }

  toggleMenuViaKeyboard() {
    this.openedViaKeyboard = true;
    this.isMenuOpen = !this.isMenuOpen;
  }

  @Watch('isMenuOpen')
  onMenuOpenChange(newVal: boolean) {
    if (newVal) {
      if (this.openedViaKeyboard) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.focusFirstMenuItem();
            this.openedViaKeyboard = false;
          }, 100);
        });
      }
    } else {
      this.showLanguageMenu = false;
    }
  }

  @Watch('showLanguageMenu')
  onLanguageMenuChange(newVal: boolean) {
    if (newVal) {
      if (this.openedViaKeyboard) {
        this.$nextTick(() => {
          // Longer delay to ensure language menu is fully rendered
          setTimeout(() => {
            this.focusFirstLanguageItem();
            this.openedViaKeyboard = false;
          }, 100);
        });
      }
    } else {
      if (this.openedViaKeyboard) {
        // Focus back on first main menu item
        this.$nextTick(() => {
          setTimeout(() => {
            this.focusFirstMenuItem();
            this.openedViaKeyboard = false;
          }, 100);
        });
      }
    }
  }

  get mainMenuItems(): MenuItem[] {
    return [
      {
        label: this.$t('nav.home') as string,
        path: '/',
        ariaLabel: this.$t('aria.navigateToHome') as string
      },
      {
        label: this.$t('nav.teachers') as string,
        path: '/teachers',
        ariaLabel: this.$t('aria.navigateToTeachers') as string
      },
      {
        label: this.$t('nav.schools') as string,
        path: '/schools',
        ariaLabel: this.$t('aria.navigateToSchools') as string
      },
      {
        label: this.$t('nav.pricing') as string,
        path: '/pricing',
        ariaLabel: this.$t('aria.navigateToPricing') as string
      },
      {
        label: this.$t('nav.mobileApps') as string,
        path: '/mobile-apps',
        ariaLabel: this.$t('aria.navigateToMobileApps') as string
      },
      {
        label: this.$t('nav.help') as string,
        path: '/help',
        ariaLabel: this.$t('aria.navigateToHelp') as string
      }
    ];
  }

  get mobileLanguages(): Language[] {
    return [
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Español' },
      { code: 'fr', name: 'Français' },
      { code: 'de', name: 'Deutsch' },
      { code: 'it', name: 'Italiano' }
    ];
  }

  closeMenu() {
    this.isMenuOpen = false;
    this.showLanguageMenu = false;
  }

  navigateTo(path: string) {
    if (this.$route.path !== path) {
      this.$router.push(path).catch((err) => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    }
    this.closeMenu();
  }

  showLanguageMenuView() {
    this.openedViaKeyboard = false;
    this.showLanguageMenu = true;
  }

  showLanguageMenuViewViaKeyboard() {
    this.openedViaKeyboard = true;
    this.showLanguageMenu = true;
  }

  showMainMenu() {
    this.openedViaKeyboard = false;
    this.showLanguageMenu = false;
  }

  showMainMenuViaKeyboard() {
    this.openedViaKeyboard = true;
    this.showLanguageMenu = false;
  }

  selectLanguage(languageCode: string) {
    localStorage.setItem('planbook-locale', languageCode);
    this.$i18n.locale = languageCode;
    updateHtmlLang(languageCode);
    this.showLanguageMenu = false;
    this.isMenuOpen = false;
  }

  private focusFirstMenuItem() {
    const homeItemRef = this.$refs['menuItem-/'] as HTMLElement[];
    if (homeItemRef && homeItemRef[0]) {
      homeItemRef[0].focus();
    }
  }

  private focusFirstLanguageItem() {
    const firstLanguageRef = this.$refs['language-en'] as HTMLElement[];
    if (firstLanguageRef && firstLanguageRef[0]) {
      firstLanguageRef[0].focus();
    }
  }

  private handleArrowDown() {
    const focusableElements = this.getFocusableMenuItems();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
    if (focusableElements[nextIndex]) {
      focusableElements[nextIndex].focus();
    }
  }

  private handleArrowUp() {
    const focusableElements = this.getFocusableMenuItems();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
    if (focusableElements[prevIndex]) {
      focusableElements[prevIndex].focus();
    }
  }

  private getFocusableMenuItems(): HTMLElement[] {
    const menuList = this.$refs.menuList as HTMLElement;
    if (!menuList) {
      return [];
    }
    const items = menuList.querySelectorAll('button[tabindex="0"]');
    return Array.from(items) as HTMLElement[];
  }

  private handleTabKey() {
    // Allow default tab behavior to move to next focusable element
    this.closeMenu();
  }

  private handleShiftTabKey() {
    // Allow default shift+tab behavior to move to previous focusable element
    this.closeMenu();
  }
}
